import { SVGAssets } from '@business/assets/vectors';
import { Button } from '@business/workflows/ComponentUI';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  icon?: ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  confirmButtonColor?: string;
  confirmButtonHoverColor?: string;
  variant?: 'delete' | 'success' | 'warning' | 'info';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  icon,
  iconBgColor,
  iconColor,
  confirmButtonColor,
  confirmButtonHoverColor,
  variant = 'delete'
}) => {
  const { t } = useTranslation();

  // Default values based on variant
  const getDefaults = () => {
    switch (variant) {
      case 'delete':
        return {
          title: title || t('mediaView.deleteAsset'),
          message: message || t('mediaView.deleteAssetTitle'),
          confirmText: confirmText || t('mediaView.yesDelete'),
          cancelText: cancelText || t('keepIt'),
          icon: icon || <SVGAssets.Delete className="w-12 h-12" />,
          iconBgColor: iconBgColor,
          iconColor: iconColor,
          confirmButtonColor: confirmButtonColor || 'bg-[#49712B]',
          confirmButtonHoverColor:
            confirmButtonHoverColor || 'hover:bg-[#49712B]'
        };
      case 'success':
        return {
          title: title || t('success'),
          message: message || t('operationSuccessful'),
          confirmText: confirmText || t('ok'),
          cancelText: cancelText || t('cancel'),
          icon: icon || <SVGAssets.AlertCheck className="w-12 h-12" />,
          iconBgColor: iconBgColor || 'bg-green-100',
          iconColor: iconColor || 'text-green-600',
          confirmButtonColor: confirmButtonColor || 'bg-[#49712B]',
          confirmButtonHoverColor:
            confirmButtonHoverColor || 'hover:bg-[#49712B]'
        };
      case 'warning':
        return {
          title: title || t('warning'),
          message: message || t('areYouSure'),
          confirmText: confirmText || t('continue'),
          cancelText: cancelText || t('cancel'),
          icon: icon || <SVGAssets.AlertWarning className="w-12 h-12" />,
          iconBgColor: iconBgColor || 'bg-yellow-100',
          iconColor: iconColor || 'text-yellow-600',
          confirmButtonColor: confirmButtonColor || 'bg-yellow-600',
          confirmButtonHoverColor:
            confirmButtonHoverColor || 'hover:bg-yellow-700'
        };
      case 'info':
        return {
          title: title || t('information'),
          message: message || t('pleaseConfirm'),
          confirmText: confirmText || t('confirm'),
          cancelText: cancelText || t('cancel'),
          icon: icon || <SVGAssets.AlertInfo className="w-12 h-12" />,
          iconBgColor: iconBgColor || 'bg-blue-100',
          iconColor: iconColor || 'text-blue-600',
          confirmButtonColor: confirmButtonColor || 'bg-blue-600',
          confirmButtonHoverColor:
            confirmButtonHoverColor || 'hover:bg-blue-700'
        };
      default:
        return {
          title: title || t('confirm'),
          message: message || t('areYouSure'),
          confirmText: confirmText || t('confirm'),
          cancelText: cancelText || t('cancel'),
          icon: icon || <SVGAssets.Info className="w-12 h-12" />,
          iconBgColor: iconBgColor || 'bg-gray-100',
          iconColor: iconColor || 'text-gray-600',
          confirmButtonColor: confirmButtonColor || 'bg-gray-600',
          confirmButtonHoverColor:
            confirmButtonHoverColor || 'hover:bg-gray-700'
        };
    }
  };

  const defaults = getDefaults();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {defaults.title}
          </h2>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            btnStyle="no-outline">
            ×
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className="mb-4 flex justify-center">
            <div
              className={`w-20 h-20 rounded-full ${defaults.iconBgColor} flex items-center justify-center`}>
              <div className={defaults.iconColor}>{defaults.icon}</div>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {defaults.title}
          </h3>
          <p className="text-sm text-gray-500">{defaults.message}</p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <Button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            btnStyle="no-outline">
            {defaults.cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            btnStyle="no-outline"
            className={`flex-1 px-4 py-2 ${defaults.confirmButtonColor} text-white rounded-md ${defaults.confirmButtonHoverColor} transition-colors`}>
            {defaults.confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
